import React from 'react';
import PropTypes from 'prop-types';
import config from '../config';
import template from '../helpers/template';

const pageRange = (selected, numPages, num) => {
    let selectedPos = Math.ceil(num / 2);
    let start = (selected < selectedPos) ? 1 : selected - selectedPos + 1;
    let len = (numPages < start + num - 1) ? numPages - start + 1 : num;

    return Array
        .apply(null, Array(len))
        .map((u, i) => start + i);
};

const PaginationItem = ({className, page, display, loading = false, onPageChange}) => {
    const handleClick = React.useCallback(() => onPageChange(page), [onPageChange, page]);
    return (
        <button
            className={'btn btn-outline btn-success' + (className ? ' ' + className : '')}
            onClick={handleClick}
            dangerouslySetInnerHTML={{__html: display + ''}}
            type="button"
            disabled={loading}
        />
    );
};

class PaginationLib extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            page: props.initialPage,
            loading: props.loading
        };

        this.onPageChange = this.onPageChange.bind(this);
        this.staticNames = { paginationInfo: '{{from}}-{{to}} {{total}}' };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.initialPage !== this.props.initialPage || prevProps.loading !== this.props.loading) {
            this.setState({
                page: this.props.initialPage,
                loading: this.props.loading
            });
        }
    }

    onPageChange(page) {
        if (this.state.page === page) {
            return;
        }

        this.setState({page: page});
        this.props.onPageChange(page);
    }

    getInfo() {
        const to = this.state.page * this.props.itemsCountPerPage;
        const total = this.props.itemsCountPerPage * this.props.total;

        return template(this.staticNames.paginationInfo, {
            from: (this.state.page - 1) * this.props.itemsCountPerPage + 1,
            to: to > total ? total : to,
            total
        });
    }

    render() {
        let self = this;
        let currentPage = this.state.page;
        let total = Math.ceil(this.props.total);

        const paginationItem = (className, page, text) => {
            return (
                <PaginationItem
                    className={className}
                    onPageChange={self.onPageChange}
                    page={page}
                    display={text}
                    key={text}
                    loading={self.state.loading}
                />
            );
        };

        let pages = pageRange(currentPage, total, this.props.pagesToDisplay)
            .map(function (p) {
                let isCurrentPage = currentPage === p;
                return paginationItem(isCurrentPage ? 'active' : '', p, p);
            });

        let firstPage = this.props.showFirstLast && currentPage !== 1 ?
            paginationItem('first', 1, this.props.firstPageText) : null;

        let prevPage = this.props.showPrevNext && currentPage > 1 ?
            paginationItem('prev', currentPage - 1, this.props.prevPageText) : null;

        let nextPage = this.props.showPrevNext && currentPage !== total ?
            paginationItem('next', currentPage + 1, this.props.nextPageText) : null;

        let lastPage = this.props.showFirstLast && currentPage !== total ?
            paginationItem('last', total, this.props.lastPageText) : null;

        return (
            <div className="pagination">
                {this.props.showInfo && this.props.align === 'right'
                    ? <div className="pagination-info">{this.getInfo()}</div>
                    : null
                }
                <div className="btn-group pagination-group">
                    {firstPage}
                    {prevPage}
                    {pages}
                    {nextPage}
                    {lastPage}
                </div>
                {this.props.showInfo && this.props.align === 'left'
                    ? <div className="pagination-info">{this.getInfo()}</div>
                    : null
                }
            </div>
        );
    }
}

PaginationLib.propTypes = {
    onPageChange: PropTypes.func.isRequired,
    total: PropTypes.number.isRequired,
    initialPage: PropTypes.number,
    itemsCountPerPage: PropTypes.number,
    pagesToDisplay: PropTypes.number,
    firstPageText: PropTypes.string,
    prevPageText: PropTypes.string,
    nextPageText: PropTypes.string,
    lastPageText: PropTypes.string,
    align: PropTypes.string,
    showFirstLast: PropTypes.bool,
    showPrevNext: PropTypes.bool,
    showInfo: PropTypes.bool,
    loading: PropTypes.bool
};

PaginationLib.defaultProps = {
    initialPage: 1,
    pagesToDisplay: 5,
    itemsCountPerPage:  config.itemsCountPerPage,
    firstPageText: '«',
    prevPageText: '<i class="fa fa-chevron-left"></i>',
    nextPageText: '<i class="fa fa-chevron-right"></i>',
    lastPageText: '»',
    align: 'left',
    showFirstLast: true,
    showPrevNext: true,
    showInfo: true,
    loading: false
};

// Removed legacy contextTypes to comply with React 19

class Pagination extends React.PureComponent {
    render() {
        return (
            this.props.totalPages > 1
                ? <PaginationLib
                    {...this.props}
                    total={this.props.totalPages}
                  />
                : null
        );
    }
}

export default Pagination;
